import { gql } from '@apollo/client/core';
import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import zvdzUseQuery from '@/common/composition/zvdzUseQuery';
import combinedBooleanRef from '@/common/combined-boolean-ref';

export type Role = {
  rolId: number;
  loginId: number;
  gebruikersnaam: string;
  organisatienaam?: string;
  organisatieId?: number;
};

type UserContext = {
  isGroteKlant: boolean;
  kanVerzuimMelden: boolean;
  isGzmKlant: boolean;
  isMkbKlant: boolean;
};

type GetSkeletonDataResponse = {
  rollen: Role[];
  currentRolId: number;
  userName: string;
  ownLoginId: number;
};

const GET_SKELETON_DATA = gql`
  query getSkeletonData {
    skeletonData(input: {})
      @rest(
        type: "GetSkeletonDataResponse"
        method: "POST"
        path: "Skeleton/GeefSkeletonData"
      ) {
      currentRolId
      rollen {
        rolId
        loginId
        gebruikersnaam
        organisatienaam
        organisatieId
      }
      userName
      ownLoginId
    }
  }
`;

const GET_USER_CONTEXT = gql`
  query getUserContext {
    #    TODO: add this data to the "Skeleton/GeefSkeletonData" endpoint
    userContext
      @rest(
        type: "UserContext"
        method: "GET"
        path: "Home?jsonOnly=true&forSkeletonData"
      ) {
      isGroteKlant
      kanVerzuimMelden
      isGzmKlant
      isMkbKlant
    }
  }
`;

type SkeletonData = {
  userContext: Ref<UserContext | undefined>;
  currentScope: ComputedRef<string | undefined>;
  scopeCurrent: ComputedRef<Role | undefined>;
  organisatieId: ComputedRef<number>;
  scopes: ComputedRef<Role[] | undefined>;
  userName: ComputedRef<string | undefined>;
  ownLoginId: ComputedRef<number | undefined>;
  loading: Ref<boolean>;
};

export default function useSkeletonData(): SkeletonData {
  const { singleResult: skeletonData, loading: loadingSkeletonData } =
    zvdzUseQuery<GetSkeletonDataResponse>(GET_SKELETON_DATA);

  const { singleResult: userContext, loading: loadingUserContext } =
    zvdzUseQuery<UserContext>(GET_USER_CONTEXT);

  const loading = combinedBooleanRef(loadingSkeletonData, loadingUserContext);

  const currentScope = computed<string | undefined>(
    () =>
      skeletonData.value &&
      `${skeletonData.value.ownLoginId}:${skeletonData.value.currentRolId}`,
  );
  const scopes = computed(() => skeletonData.value?.rollen);
  const userName = computed(() => skeletonData.value?.userName);
  const ownLoginId = computed(() => skeletonData.value?.ownLoginId);

  const scopeCurrent = computed(() =>
    scopes.value?.find(
      ({ rolId }) => rolId === skeletonData.value?.currentRolId,
    ),
  );

  const organisatieId = computed(() => scopeCurrent.value?.organisatieId || -1);

  return {
    userContext,
    currentScope,
    scopeCurrent,
    organisatieId,
    scopes,
    userName,
    ownLoginId,
    loading,
  };
}
